import { useState, useCallback, useRef } from 'react';
import { artService, ArtGenerationRequest, ArtGenerationResponse } from '../services/artService';

export interface ArtGenerationState {
  isGenerating: boolean;
  currentPrediction: ArtGenerationResponse | null;
  generatedImage: string | null;
  error: string | null;
  progress: string;
}

export interface ArtGenerationControls {
  generateArt: (request: ArtGenerationRequest) => Promise<void>;
  cancelGeneration: () => Promise<void>;
  clearResults: () => void;
  reset: () => void;
}

export const useArtGeneration = (): ArtGenerationState & ArtGenerationControls => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentPrediction, setCurrentPrediction] = useState<ArtGenerationResponse | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);

  const generateArt = useCallback(async (request: ArtGenerationRequest) => {
    try {
      setIsGenerating(true);
      setError(null);
      setProgress('Initializing art generation...');
      setCurrentPrediction(null);
      setGeneratedImage(null);

      // Create abort controller for this generation
      abortControllerRef.current = new AbortController();

      const result = await artService.generateArtAndWait(
        request,
        (status: ArtGenerationResponse) => {
          setCurrentPrediction(status);
          
          // Update progress based on status
          switch (status.status) {
            case 'starting':
              setProgress('Starting art generation...');
              break;
            case 'processing':
              setProgress('Creating your meditation art...');
              break;
            case 'succeeded':
              setProgress('Art generation completed!');
              // Handle new format from backend
              if (status.art?.s3Key) {
                // New format: just use the s3Key directly
                setGeneratedImage(status.art.s3Key);
              } else if (status.art?.half) {
                // Old format
                setGeneratedImage(status.art.half);
              } else if (status.output) {
                // Fallback to legacy format
                setGeneratedImage(status.local_url || status.output);
              }
              break;
            case 'failed':
              setProgress('Art generation failed');
              setError(status.error || 'Art generation failed');
              break;
            case 'canceled':
              setProgress('Art generation cancelled');
              break;
            default:
              setProgress(`Status: ${status.status}`);
          }
        }
      );

      setCurrentPrediction(result);

      // Handle new response format from backend
      if (result.success && result.art?.s3Key) {
        // New format: just use the s3Key directly
        setGeneratedImage(result.art.s3Key);
        setProgress('Art generation completed successfully!');
      } else if (result.success && result.art?.half) {
        // Old format
        setGeneratedImage(result.art.half);
        setProgress('Art generation completed successfully!');
      } else if (result.status === 'succeeded' && result.output) {
        // Fallback to legacy format
        setGeneratedImage(result.local_url || result.output);
        setProgress('Art generation completed successfully!');
      } else if (result.status === 'failed' || !result.success) {
        setError(result.error || 'Art generation failed');
        setProgress('Art generation failed');
      }

    } catch (err) {
      console.error('Art generation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate art';
      setError(errorMessage);
      setProgress('Art generation failed');
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  }, []);

  const cancelGeneration = useCallback(async () => {
    if (!currentPrediction?.id || !isGenerating) {
      return;
    }

    try {
      setProgress('Cancelling art generation...');
      await artService.cancelArtGeneration(currentPrediction.id);
      setProgress('Art generation cancelled');
      setIsGenerating(false);
    } catch (err) {
      console.error('Failed to cancel art generation:', err);
      setError('Failed to cancel art generation');
    }
  }, [currentPrediction?.id, isGenerating]);

  const clearResults = useCallback(() => {
    setGeneratedImage(null);
    setCurrentPrediction(null);
    setError(null);
    setProgress('');
  }, []);

  const reset = useCallback(() => {
    setIsGenerating(false);
    setCurrentPrediction(null);
    setGeneratedImage(null);
    setError(null);
    setProgress('');
    abortControllerRef.current = null;
  }, []);

  return {
    // State
    isGenerating,
    currentPrediction,
    generatedImage,
    error,
    progress,
    
    // Controls
    generateArt,
    cancelGeneration,
    clearResults,
    reset
  };
};